import { useState, useEffect, useRef } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Property, Expense } from "@shared/schema";
import { RENOVATION_ITEM_TYPES, RENOVATION_ITEM_LABELS, TEAM_ROLES } from "@shared/schema";
import type { RenovationItem, RenovationDefaults, RenovationSettings } from "@shared/schema";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DocumentUploadDialog } from "@/components/document-upload-dialog";
import { ManageDocumentDialog } from "@/components/manage-document-dialog";
import AddressAutocomplete from "@/components/address-autocomplete";
import { setPendingBidFile, takePendingBidFile } from "@/lib/pending-bid-upload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Hammer,
  Building2,
  ChevronRight,
  GripVertical,
  Plus,
  Trash2,
  Settings2,
  DollarSign,
  ExternalLink,
  FileSearch,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  AlertTriangle,
  Lock,
  Unlock,
  Undo2,
  MapPin,
  Upload,
  Loader2,
} from "lucide-react";
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";

const DEFAULT_DAYS: Record<string, number> = {
  building: 30,
  bedrooms: 7,
  kitchen_remodel: 21,
  roof_replacement: 14,
  hvac: 5,
  plumbing: 7,
  electrical: 7,
  flooring: 7,
  painting: 5,
  windows_doors: 5,
  bathroom_remodel: 14,
  landscaping: 5,
  driveway_parking: 7,
  foundation: 14,
  insulation: 5,
  siding_exterior: 10,
  appliances: 2,
  fire_safety_code: 3,
  pest_treatment: 2,
  general_demolition: 5,
};

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function calcProjectedLaunch(startDate: string, items: RenovationItem[], permitsRequired: boolean, permitDays: number): string {
  const totalItemDays = items.reduce((sum, it) => sum + (it.estimatedDays || 0), 0);
  const permitTotal = permitsRequired ? (permitDays || 45) : 0;
  return addDays(startDate, totalItemDays + permitTotal);
}

function fmt$(val: number): string {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

function DefaultsTab() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const { toast } = useToast();
  const defaultsQuery = useQuery<RenovationDefaults>({ queryKey: ["/api/app/renovation-defaults"] });
  const defaults = defaultsQuery.data;

  const [localDays, setLocalDays] = useState<Record<string, number>>({});
  const [localPermitDays, setLocalPermitDays] = useState(45);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (defaults) {
      setLocalDays({ ...DEFAULT_DAYS, ...(defaults.defaultDays || {}) });
      setLocalPermitDays(defaults.defaultPermitDays || 45);
      setDirty(false);
    }
  }, [defaults]);

  const saveMutation = useMutation({
    mutationFn: async () => apiRequest("PUT", "/api/app/renovation-defaults", { defaultDays: localDays, defaultPermitDays: localPermitDays }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/renovation-defaults"] });
      toast({ title: "Defaults saved" });
      setDirty(false);
    },
    onError: () => toast({ title: "Failed to save defaults", variant: "destructive" }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Renovation Defaults</h2>
          <p className="text-sm text-gray-500 mt-1">Set default estimated days for each renovation item type. These pre-fill when you add items to a renovation plan.</p>
        </div>
        {dirty && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { if (defaults) { setLocalDays({ ...DEFAULT_DAYS, ...(defaults.defaultDays || {}) }); setLocalPermitDays(defaults.defaultPermitDays || 45); setDirty(false); } }} data-testid="button-defaults-cancel">Cancel</Button>
            <Button size="sm" onClick={() => saveMutation.mutate()} disabled={readOnly || (saveMutation.isPending)} data-testid="button-save-defaults">
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </div>
      <div className="bg-white border rounded-xl p-6" data-tour="renovations-defaults-panel">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {RENOVATION_ITEM_TYPES.map((type) => (
            <div key={type} className="flex items-center gap-2">
              <label className="text-sm text-gray-700 flex-1 truncate" data-testid={`label-default-${type}`}>{RENOVATION_ITEM_LABELS[type]}</label>
              <Input
                type="number"
                min={1}
                className="w-20 text-sm"
                value={localDays[type] ?? DEFAULT_DAYS[type] ?? 7}
                onChange={(e) => { setLocalDays((prev) => ({ ...prev, [type]: parseInt(e.target.value) || 1 })); setDirty(true); }}
                data-testid={`input-default-days-${type}`}
              />
              <span className="text-xs text-gray-400 w-8">days</span>
            </div>
          ))}
        </div>
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Default Permit Days</label>
            <Input
              type="number"
              min={1}
              className="w-24 text-sm"
              value={localPermitDays}
              onChange={(e) => { setLocalPermitDays(parseInt(e.target.value) || 45); setDirty(true); }}
              data-testid="input-default-permit-days"
            />
            <span className="text-xs text-gray-400 w-8">days</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Typical permit timelines are 30–60 days.</p>
        </div>
      </div>
    </div>
  );
}

function SettingsTab() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const { toast } = useToast();
  const settingsQuery = useQuery<RenovationSettings>({ queryKey: ["/api/app/renovation-settings"] });
  const settings = settingsQuery.data;

  const [localRoles, setLocalRoles] = useState<string[]>(["owner", "admin"]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings) {
      setLocalRoles(settings.unlockRoles || ["owner", "admin"]);
      setDirty(false);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async () => apiRequest("PUT", "/api/app/renovation-settings", { unlockRoles: localRoles }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/renovation-settings"] });
      toast({ title: "Settings saved" });
      setDirty(false);
    },
    onError: () => toast({ title: "Failed to save settings", variant: "destructive" }),
  });

  function toggleRole(role: string) {
    setLocalRoles((prev) => {
      const next = prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role];
      return next;
    });
    setDirty(true);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Renovation Settings</h2>
          <p className="text-sm text-gray-500 mt-1">Configure permissions and behavior for the renovation system.</p>
        </div>
        {dirty && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { if (settings) { setLocalRoles(settings.unlockRoles || ["owner", "admin"]); setDirty(false); } }} data-testid="button-settings-cancel">Cancel</Button>
            <Button size="sm" onClick={() => saveMutation.mutate()} disabled={readOnly || (saveMutation.isPending)} data-testid="button-save-settings">
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </div>
      <div className="bg-white border rounded-xl p-6 space-y-6" data-tour="renovations-settings-panel">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Unlock Permissions</h3>
          <p className="text-xs text-gray-500 mb-3">Select which team member roles are allowed to unlock a locked renovation. Locked renovations prevent accidental changes to days and budgets.</p>
          <div className="space-y-2">
            {TEAM_ROLES.map((role) => (
              <div key={role} className="flex items-center gap-3">
                <Checkbox
                  checked={localRoles.includes(role)}
                  onCheckedChange={() => toggleRole(role)}
                  disabled={role === "owner"}
                  data-testid={`checkbox-unlock-role-${role}`}
                />
                <label className="text-sm text-gray-700">{ROLE_LABELS[role] || role}</label>
                {role === "owner" && <span className="text-xs text-gray-400">(always allowed)</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkAddItemsDialog({
  open,
  onClose,
  existingTypes,
  defaultDays,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  existingTypes: Set<string>;
  defaultDays: Record<string, number>;
  onAdd: (items: Array<{ itemType: string; itemName: string; estimatedDays: number; budget: string | null; bufferPercent: string | null }>) => void;
}) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const [selections, setSelections] = useState<Record<string, { checked: boolean; days: number; budget: string; bufferPercent: string }>>({});

  useEffect(() => {
    if (open) {
      const init: Record<string, { checked: boolean; days: number; budget: string; bufferPercent: string }> = {};
      RENOVATION_ITEM_TYPES.forEach((type) => {
        if (!existingTypes.has(type)) {
          init[type] = { checked: false, days: defaultDays[type] || 7, budget: "", bufferPercent: "0" };
        }
      });
      setSelections(init);
    }
  }, [open, existingTypes, defaultDays]);

  const availableTypes = RENOVATION_ITEM_TYPES.filter((t) => !existingTypes.has(t));
  const selectedCount = Object.values(selections).filter((s) => s.checked).length;

  function handleAdd() {
    const toAdd = availableTypes
      .filter((type) => selections[type]?.checked)
      .map((type) => ({
        itemType: type,
        itemName: RENOVATION_ITEM_LABELS[type],
        estimatedDays: selections[type].days,
        budget: selections[type].budget || null,
        bufferPercent: selections[type].bufferPercent || null,
      }));
    onAdd(toAdd);
  }

  function toggleAll(checked: boolean) {
    setSelections((prev) => {
      const next = { ...prev };
      for (const type of availableTypes) {
        if (next[type]) next[type] = { ...next[type], checked };
      }
      return next;
    });
  }

  if (availableTypes.length === 0) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Renovation Items</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 py-4">All available renovation items have already been added to this project.</p>
          <DialogFooter><Button onClick={onClose}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Renovation Items</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500 mb-3">Select items to add, adjust days and budget, then click Add Items.</p>

        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_80px_100px_70px] gap-x-3 items-center px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
            <div className="w-5">
              <Checkbox
                checked={selectedCount === availableTypes.length && availableTypes.length > 0}
                onCheckedChange={(c) => toggleAll(!!c)}
                data-testid="checkbox-select-all-items"
              />
            </div>
            <div>Item</div>
            <div className="text-right">Days</div>
            <div className="text-right">Budget</div>
            <div className="text-right">Buffer %</div>
          </div>
          {availableTypes.map((type) => {
            const sel = selections[type];
            if (!sel) return null;
            return (
              <div
                key={type}
                className={`grid grid-cols-[auto_1fr_80px_100px_70px] gap-x-3 items-center px-3 py-2.5 border-b last:border-b-0 transition-colors ${sel.checked ? "bg-amber-50" : "hover:bg-gray-50"}`}
              >
                <div className="w-5">
                  <Checkbox
                    checked={sel.checked}
                    onCheckedChange={(c) => setSelections((prev) => ({ ...prev, [type]: { ...prev[type], checked: !!c } }))}
                    data-testid={`checkbox-bulk-${type}`}
                  />
                </div>
                <span className="text-sm text-gray-800">{RENOVATION_ITEM_LABELS[type]}</span>
                <Input
                  type="number"
                  min={1}
                  className="w-20 h-7 text-xs px-1 text-right ml-auto"
                  value={sel.days}
                  onChange={(e) => setSelections((prev) => ({ ...prev, [type]: { ...prev[type], days: parseInt(e.target.value) || 1 } }))}
                  data-testid={`input-bulk-days-${type}`}
                />
                <Input
                  type="number"
                  min={0}
                  step="100"
                  className="w-24 h-7 text-xs px-1 text-right ml-auto"
                  value={sel.budget}
                  placeholder="$0"
                  onChange={(e) => setSelections((prev) => ({ ...prev, [type]: { ...prev[type], budget: e.target.value } }))}
                  data-testid={`input-bulk-budget-${type}`}
                />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  className="w-16 h-7 text-xs px-1 text-right ml-auto"
                  value={sel.bufferPercent}
                  placeholder="0"
                  onChange={(e) => setSelections((prev) => ({ ...prev, [type]: { ...prev[type], bufferPercent: e.target.value } }))}
                  data-testid={`input-bulk-buffer-${type}`}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAdd} disabled={readOnly || selectedCount === 0} data-testid="button-confirm-bulk-add">
            Add {selectedCount} Item{selectedCount !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddExpenseDialog({
  propertyId,
  open,
  onClose,
  items,
  expensesByItem,
  onExpenseAdded,
}: {
  propertyId: number;
  open: boolean;
  onClose: () => void;
  items: RenovationItem[];
  expensesByItem: Record<number, number>;
  onExpenseAdded: (addedAmount: number, itemId: number) => void;
}) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const { toast } = useToast();
  const [selectedItemId, setSelectedItemId] = useState("");
  const [form, setForm] = useState({
    amount: "",
    date: new Date().toISOString().split("T")[0],
    vendor: "",
    notes: "",
  });
  const [showOverBudgetAlert, setShowOverBudgetAlert] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedItemId("");
      setForm({ amount: "", date: new Date().toISOString().split("T")[0], vendor: "", notes: "" });
    }
  }, [open]);

  const selectedItem = items.find((i) => i.id === Number(selectedItemId));
  const currentSpent = selectedItem ? (expensesByItem[selectedItem.id] || 0) : 0;
  const budgetAmount = selectedItem?.budget ? parseFloat(selectedItem.budget) : 0;
  const bufferPctItem = selectedItem?.bufferPercent ? parseFloat(selectedItem.bufferPercent) : 0;
  const bufferedBudgetAmount = budgetAmount > 0 ? budgetAmount * (1 + bufferPctItem / 100) : 0;

  const createMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/app/expenses", {
      category: "capex",
      description: selectedItem ? `${selectedItem.itemName}` : "",
      amount: form.amount,
      date: form.date,
      vendor: form.vendor || undefined,
      notes: form.notes || undefined,
      propertyId,
      renovationItemId: selectedItem?.id || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      toast({ title: "Expense added" });
      const addedAmount = parseFloat(form.amount) || 0;
      const itemId = selectedItem?.id || 0;
      onClose();
      onExpenseAdded(addedAmount, itemId);
    },
    onError: () => toast({ title: "Failed to add expense", variant: "destructive" }),
  });

  function handleSubmit() {
    if (!selectedItemId) {
      toast({ title: "Please select a budget item", variant: "destructive" });
      return;
    }
    if (!form.amount || !form.date) {
      toast({ title: "Amount and date are required", variant: "destructive" });
      return;
    }

    const newAmount = parseFloat(form.amount) || 0;
    const effectiveBudget = bufferedBudgetAmount > 0 ? bufferedBudgetAmount : budgetAmount;
    if (effectiveBudget > 0 && (currentSpent + newAmount) > effectiveBudget && !showOverBudgetAlert) {
      setShowOverBudgetAlert(true);
      return;
    }

    createMutation.mutate();
  }

  function confirmOverBudget() {
    setShowOverBudgetAlert(false);
    createMutation.mutate();
  }

  const newAmount = parseFloat(form.amount) || 0;
  const projectedTotal = currentSpent + newAmount;
  const effectiveBudgetDisplay = bufferedBudgetAmount > 0 ? bufferedBudgetAmount : budgetAmount;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && createMutation.isPending) return;
          if (!v) onClose();
        }}
      >
        <DialogContent
          className="max-w-md max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (createMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (createMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (createMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Add Project Expense (CapEx)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Category</label>
              <div className="px-3 py-2 bg-gray-50 border rounded-md text-sm text-gray-700" data-testid="text-expense-category">CapEx (Capital Expenditure)</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Budget Item *</label>
              <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                <SelectTrigger data-testid="select-expense-budget-item">
                  <SelectValue placeholder="Select a renovation item..." />
                </SelectTrigger>
                <SelectContent>
                  {items.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>{item.itemName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedItem && (
              <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm" data-testid="text-budget-info">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Budget:</span>
                  <span className="font-medium">{budgetAmount > 0 ? fmt$(budgetAmount) : "Not set"}</span>
                </div>
                {bufferPctItem > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Buffered Budget ({bufferPctItem}%):</span>
                    <span className="font-medium text-amber-700">{fmt$(bufferedBudgetAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Spent so far:</span>
                  <span className="font-medium">{fmt$(currentSpent)}</span>
                </div>
                {(bufferedBudgetAmount > 0 || budgetAmount > 0) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Remaining:</span>
                    <span className={`font-medium ${(bufferedBudgetAmount || budgetAmount) - currentSpent <= 0 ? "text-red-600" : "text-green-600"}`}>
                      {fmt$((bufferedBudgetAmount || budgetAmount) - currentSpent)}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Amount *</label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} placeholder="0.00" data-testid="input-expense-amount" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Date *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="input-expense-date" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Vendor</label>
              <Input value={form.vendor} onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))} placeholder="Vendor name" data-testid="input-expense-vendor" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." data-testid="textarea-expense-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={readOnly || createMutation.isPending} data-testid="button-submit-expense">
              {createMutation.isPending ? "Saving..." : "Log Expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showOverBudgetAlert} onOpenChange={setShowOverBudgetAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Over Budget Warning
            </AlertDialogTitle>
            <AlertDialogDescription>
              This expense of {fmt$(newAmount)} will put <strong>{selectedItem?.itemName}</strong> over budget.
              <br /><br />
              {bufferPctItem > 0 ? `Buffered Budget (${bufferPctItem}%): ${fmt$(effectiveBudgetDisplay)}` : `Budget: ${fmt$(effectiveBudgetDisplay)}`}<br />
              Already spent: {fmt$(currentSpent)}<br />
              This expense: {fmt$(newAmount)}<br />
              <strong>New total: {fmt$(projectedTotal)} ({effectiveBudgetDisplay > 0 ? Math.round((projectedTotal / effectiveBudgetDisplay) * 100) : 0}% of budget)</strong>
              <br /><br />
              Do you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={readOnly} onClick={confirmOverBudget} data-testid="button-confirm-over-budget">Proceed Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RenovationDetail({ propertyId }: { propertyId: number }) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [uploadDialog, setUploadDialog] = useState<{ open: boolean; mode: "invoice" | "bid"; renovationItemId?: number | null }>({ open: false, mode: "invoice", renovationItemId: null });
  const [autoBidFile, setAutoBidFile] = useState<File | null>(null);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [showStartPrompt, setShowStartPrompt] = useState(false);
  const [showBudgetPaidPrompt, setShowBudgetPaidPrompt] = useState(false);
  const [showLaunchPrompt, setShowLaunchPrompt] = useState(false);
  const pendingBudgetCheckRef = useRef(false);
  const [showPullFromUW, setShowPullFromUW] = useState(false);
  const [uwTransferItems, setUwTransferItems] = useState<Array<{ key: string; itemType: string; itemName: string; budget: number; bufferPercent: number; checked: boolean }>>([]);
  const [pullingFromUW, setPullingFromUW] = useState(false);
  const [showLockPrompt, setShowLockPrompt] = useState(false);
  const [manageDocId, setManageDocId] = useState<number | null>(null);

  const propertyQuery = useQuery<Property>({
    queryKey: ["/api/app/properties", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
  });
  usePageTitle(propertyQuery.data?.address || "Renovation");

  // Add to Renovations → Upload Contractor Bid: when redirected here with
  // ?uploadBid=1, open the bid upload dialog pre-loaded with the carried-over
  // file so extraction starts automatically. Strip the param so a refresh
  // doesn't re-open the dialog.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("uploadBid") === "1") {
      const f = takePendingBidFile();
      setAutoBidFile(f);
      setUploadDialog({ open: true, mode: "bid", renovationItemId: null });
      params.delete("uploadBid");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showVoided, setShowVoided] = useState(false);
  const itemsQuery = useQuery<RenovationItem[]>({
    queryKey: showVoided
      ? ["/api/app/properties", propertyId, "renovation-items", "with-voided"]
      : ["/api/app/properties", propertyId, "renovation-items"],
    queryFn: async () => {
      const url = `/api/app/properties/${propertyId}/renovation-items${showVoided ? "?includeVoided=true" : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const restoreDocMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (documentId: number) => {
      const res = await apiRequest("POST", `/api/app/property-documents/${documentId}/restore`, {});
      return res.json() as Promise<{ restoredExpenseIds: number[]; restoredRenovationItemIds: number[] }>;
    },
    onSuccess: (r) => {
      toast({ title: "Restored", description: `${r.restoredExpenseIds.length} expense(s) and ${r.restoredRenovationItemIds.length} renovation item(s) restored.` });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items", "with-voided"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
    },
    onError: (e: any) => toast({ title: "Restore failed", description: parseErrorMessage(e, "Couldn't restore this document. Please try again."), variant: "destructive" }),
  });

  const expensesQuery = useQuery<Expense[]>({ queryKey: ["/api/app/expenses"] });
  const defaultsQuery = useQuery<RenovationDefaults>({ queryKey: ["/api/app/renovation-defaults"] });
  const settingsQuery = useQuery<RenovationSettings>({ queryKey: ["/api/app/renovation-settings"] });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest("PATCH", `/api/app/properties/${propertyId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const addItemMutation = useMutation({
    mutationFn: async (data: { itemType: string; itemName: string; estimatedDays: number; sortOrder: number; budget?: string | null; bufferPercent?: string | null }) => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/renovation-items`, data);
      return res.json() as Promise<RenovationItem>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items"] });
    },
    onError: () => toast({ title: "Failed to add item", variant: "destructive" }),
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/app/renovation-items/${id}`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items"] }),
    onError: () => toast({ title: "Failed to update item", variant: "destructive" }),
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/renovation-items/${id}`);
      return id;
    },
    onSuccess: (deletedId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items"] });
      const prop = propertyQuery.data;
      if (prop?.renovationStartDate) {
        const currentItems = itemsQuery.data || [];
        const updated = currentItems.filter((i) => i.id !== deletedId);
        const suggested = calcProjectedLaunch(prop.renovationStartDate, updated, prop.permitsRequired ?? false, prop.permitDays ?? 45);
        updateMutation.mutate({ projectedLaunchDate: suggested });
      }
    },
    onError: () => toast({ title: "Failed to delete item", variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: number[]) =>
      apiRequest("POST", `/api/app/properties/${propertyId}/renovation-items/reorder`, { orderedIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items"] }),
  });

  if (propertyQuery.isLoading) return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-[300px] w-full" />
    </div>
  );

  const property = propertyQuery.data;
  if (!property) return (
    <div className="text-center py-20">
      <p className="text-gray-500">Property not found</p>
      <Button variant="ghost" asChild data-testid="button-back-to-renovations"><Link href="/app/renovations">Back</Link></Button>
    </div>
  );

  const allItems = itemsQuery.data || [];
  const items = allItems.filter((i) => !i.voidedAt);
  const voidedItems = allItems.filter((i) => !!i.voidedAt);
  const allExpenses = expensesQuery.data || [];
  const defaults = defaultsQuery.data;
  const defaultDays = { ...DEFAULT_DAYS, ...(defaults?.defaultDays || {}) };
  const isLocked = property.renovationLocked ?? false;

  const propertyExpenses = allExpenses.filter((e) => e.propertyId === propertyId && e.category === "capex");
  const expensesByItem: Record<number, number> = {};
  propertyExpenses.forEach((exp) => {
    if (exp.renovationItemId) {
      expensesByItem[exp.renovationItemId] = (expensesByItem[exp.renovationItemId] || 0) + (parseFloat(exp.amount) || 0);
    }
  });

  const saveField = (field: string, value: unknown) => updateMutation.mutate({ [field]: value });

  const statusColor: Record<string, string> = {
    not_started: "bg-gray-100 text-gray-600",
    in_progress: "bg-amber-100 text-amber-700",
    complete: "bg-green-100 text-green-700",
  };
  const statusLabel: Record<string, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    complete: "Complete",
  };

  const permitsRequired = property.permitsRequired ?? false;
  const permitDays = property.permitDays ?? 45;
  const renovationStartDate = property.renovationStartDate || "";
  const projectedLaunch = property.projectedLaunchDate || (renovationStartDate ? calcProjectedLaunch(renovationStartDate, items, permitsRequired, permitDays) : "");

  const existingTypes = new Set(items.map((i) => i.itemType));

  function handleBulkAdd(newItems: Array<{ itemType: string; itemName: string; estimatedDays: number; budget: string | null; bufferPercent: string | null }>) {
    const startIdx = items.length;
    let addedCount = 0;
    newItems.forEach((item, idx) => {
      addItemMutation.mutate({
        itemType: item.itemType,
        itemName: item.itemName,
        estimatedDays: item.estimatedDays,
        sortOrder: startIdx + idx,
        budget: item.budget,
        bufferPercent: item.bufferPercent || null,
      });
      addedCount++;
    });
    setShowBulkAdd(false);
    if (addedCount > 0) {
      toast({ title: `Adding ${addedCount} item${addedCount > 1 ? "s" : ""}...` });
    }
  }

  function handleToggleComplete(item: RenovationItem) {
    const nowCompleted = !item.completed;
    updateItemMutation.mutate({
      id: item.id,
      data: {
        completed: nowCompleted,
        completedDate: nowCompleted ? new Date().toISOString().split("T")[0] : null,
      },
    });
  }

  function handleDragStart(id: number) {
    setDraggedId(id);
  }

  function handleDragOver(e: React.DragEvent, id: number) {
    e.preventDefault();
    setDragOverId(id);
  }

  function handleDrop(targetId: number) {
    if (draggedId === null || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const newOrder = [...items];
    const fromIdx = newOrder.findIndex((i) => i.id === draggedId);
    const toIdx = newOrder.findIndex((i) => i.id === targetId);
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    reorderMutation.mutate(newOrder.map((i) => i.id));
    setDraggedId(null);
    setDragOverId(null);
  }

  function recalcAndSaveLaunch(startDate: string, itms: RenovationItem[], perRequired: boolean, perDays: number) {
    if (!startDate) return;
    const suggested = calcProjectedLaunch(startDate, itms, perRequired, perDays);
    saveField("projectedLaunchDate", suggested);
  }

  function toggleLock() {
    saveField("renovationLocked", !isLocked);
    toast({ title: isLocked ? "Renovation unlocked" : "Renovation locked" });
  }

  function handleExpenseAdded(addedAmount: number, _itemId: number) {
    const currentStatus = property?.renovationStatus;
    const prevTotalSpent = items.reduce((sum, item) => sum + (expensesByItem[item.id] || 0), 0);
    const isFirstExpense = prevTotalSpent === 0 && addedAmount > 0;
    const newTotalSpent = prevTotalSpent + addedAmount;
    const currentTotalBudget = items.reduce((sum, item) => sum + (item.budget ? parseFloat(item.budget) || 0 : 0), 0);
    const budgetFullyPaid = currentTotalBudget > 0 && newTotalSpent >= currentTotalBudget;

    if (isFirstExpense && (!currentStatus || currentStatus === "not_started")) {
      if (budgetFullyPaid) {
        pendingBudgetCheckRef.current = true;
      }
      setTimeout(() => setShowStartPrompt(true), 300);
    } else if (budgetFullyPaid && currentStatus !== "complete") {
      setTimeout(() => setShowBudgetPaidPrompt(true), 300);
    }
  }

  function handleConfirmInProgress() {
    setShowStartPrompt(false);
    updateMutation.mutate({ renovationStatus: "in_progress" }, {
      onSuccess: () => {
        toast({ title: "Renovation status updated to In Progress" });
        if (pendingBudgetCheckRef.current) {
          pendingBudgetCheckRef.current = false;
          setTimeout(() => setShowBudgetPaidPrompt(true), 300);
        }
      },
    });
  }

  function handleDismissStartPrompt() {
    setShowStartPrompt(false);
    if (pendingBudgetCheckRef.current) {
      pendingBudgetCheckRef.current = false;
      setTimeout(() => setShowBudgetPaidPrompt(true), 300);
    }
  }

  function handleConfirmComplete() {
    setShowBudgetPaidPrompt(false);
    updateMutation.mutate({ renovationStatus: "complete" }, {
      onSuccess: () => {
        toast({ title: "Renovation marked as complete" });
        setTimeout(() => setShowLaunchPrompt(true), 300);
      },
    });
  }

  function handleLogExpenseClick() {
    if (!isLocked) {
      setShowLockPrompt(true);
    } else {
      setShowAddExpense(true);
    }
  }

  function handleLockAndLogExpense() {
    setShowLockPrompt(false);
    updateMutation.mutate({ renovationLocked: true }, {
      onSuccess: () => {
        toast({ title: "Renovation locked" });
        setShowAddExpense(true);
      },
      onError: () => {
        toast({ title: "Failed to lock renovation", variant: "destructive" });
      },
    });
  }

  function handleConfirmLaunch() {
    setShowLaunchPrompt(false);
    updateMutation.mutate({ status: "launch" }, {
      onSuccess: (data: unknown) => {
        toast({ title: "Property moved to Launch" });
        const padsplitUnlinked = (data && typeof data === "object" && "warnings" in (data as Record<string, unknown>))
          ? (data as { warnings?: { padsplitUnlinked?: { assistantName?: string | null } } }).warnings?.padsplitUnlinked
          : undefined;
        if (padsplitUnlinked) {
          const assistantName = padsplitUnlinked.assistantName;
          const desc = assistantName
            ? `Invite ${assistantName} (${assistantName}@appmail.padpulse.io) as a team member in PadSplit so data can sync.`
            : "Invite your PadPulse assistant as a team member in PadSplit to enable syncing.";
          toast({
            title: "Link this property to PadSplit",
            description: desc,
          });
        }
        navigate(`/app/property-launch/${propertyId}`);
      },
    });
  }

  function nVal(val: string | null | undefined): number {
    if (!val) return 0;
    const parsed = parseFloat(String(val).replace(/[^0-9.\-]/g, ""));
    return isNaN(parsed) ? 0 : parsed;
  }

  async function handlePullFromUnderwriting() {
    try {
      const res = await fetch(`/api/app/underwriting/${propertyId}`, { credentials: "include" });
      if (!res.ok) {
        toast({ title: "No underwriting analysis found for this property", variant: "destructive" });
        return;
      }
      const a = await res.json();

      const currentBed = Number(a.currentBedrooms) || 0;
      const futureBed = Number(a.futureBedrooms) || 0;
      const currentBath = Number(a.currentBathrooms) || 0;
      const futureBath = Number(a.futureBathrooms) || 0;
      const roomAddQty = Math.max(0, futureBed - currentBed);
      const roomAddCost = nVal(a.roomAdditionsCost);
      const bathAddQty = Math.max(0, futureBath - currentBath);
      const bathAddCost = nVal(a.bathAdditionsCost);
      const kitchenQty = Number(a.kitchenQty) || 0;
      const kitchenAmt = nVal(a.kitchenAmount);
      const hvacQty = Number(a.hvacQty) || 0;
      const hvacAmt = nVal(a.hvacAmount);
      const roofQty = Number(a.roofQty) || 0;
      const roofAmt = nVal(a.roofAmount);
      const plumbingQty = Number(a.plumbingQty) || 0;
      const plumbingAmt = nVal(a.plumbingAmount);
      const foundationQty = Number(a.foundationRepairQty) || 0;
      const foundationAmt = nVal(a.foundationRepairAmount);
      const otherQty = Number(a.otherQty) || 0;
      const otherAmt = nVal(a.otherAmount);
      const purchasePrice = nVal(a.homePurchasePrice);
      const additionalRehabPct = nVal(a.additionalRehabAmount) / 100;
      const additionalRehab = purchasePrice * additionalRehabPct;

      const roomAdditionsTotal = roomAddQty * roomAddCost;
      const bathAdditionsTotal = bathAddQty * bathAddCost;
      const kitchenTotal = kitchenQty * kitchenAmt;
      const hvacTotal = hvacQty * hvacAmt;
      const roofTotal = roofQty * roofAmt;
      const plumbingTotal = plumbingQty * plumbingAmt;
      const foundationTotal = foundationQty * foundationAmt;
      const otherTotal = otherQty * otherAmt;
      const uwBufferPctValue = nVal(a.bufferPercent);
      const transferItemsList: Array<{ key: string; itemType: string; itemName: string; budget: number; bufferPercent: number; checked: boolean }> = [];
      if (roomAdditionsTotal > 0) transferItemsList.push({ key: "bedrooms", itemType: "bedrooms", itemName: RENOVATION_ITEM_LABELS.bedrooms, budget: roomAdditionsTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (bathAdditionsTotal > 0) transferItemsList.push({ key: "bathroom_remodel", itemType: "bathroom_remodel", itemName: RENOVATION_ITEM_LABELS.bathroom_remodel, budget: bathAdditionsTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (kitchenTotal > 0) transferItemsList.push({ key: "kitchen_remodel", itemType: "kitchen_remodel", itemName: RENOVATION_ITEM_LABELS.kitchen_remodel, budget: kitchenTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (hvacTotal > 0) transferItemsList.push({ key: "hvac", itemType: "hvac", itemName: RENOVATION_ITEM_LABELS.hvac, budget: hvacTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (roofTotal > 0) transferItemsList.push({ key: "roof_replacement", itemType: "roof_replacement", itemName: RENOVATION_ITEM_LABELS.roof_replacement, budget: roofTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (plumbingTotal > 0) transferItemsList.push({ key: "plumbing", itemType: "plumbing", itemName: RENOVATION_ITEM_LABELS.plumbing, budget: plumbingTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (foundationTotal > 0) transferItemsList.push({ key: "foundation", itemType: "foundation", itemName: RENOVATION_ITEM_LABELS.foundation, budget: foundationTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (otherTotal > 0) transferItemsList.push({ key: "general_demolition", itemType: "general_demolition", itemName: "Other / General", budget: otherTotal, bufferPercent: uwBufferPctValue, checked: true });
      if (additionalRehab > 0) transferItemsList.push({ key: "additional_rehab", itemType: "painting", itemName: "Additional Rehab", budget: additionalRehab, bufferPercent: uwBufferPctValue, checked: true });

      if (transferItemsList.length === 0) {
        toast({ title: "No CapEx items found in underwriting analysis", variant: "destructive" });
        return;
      }

      const existing = new Set(items.map((i) => i.itemType));
      transferItemsList.forEach((it) => {
        if (existing.has(it.itemType)) it.checked = false;
      });

      setUwTransferItems(transferItemsList);
      setShowPullFromUW(true);
    } catch {
      toast({ title: "Failed to load underwriting data", variant: "destructive" });
    }
  }

  async function confirmPullFromUW() {
    setPullingFromUW(true);
    try {
      const checked = uwTransferItems.filter((it) => it.checked);
      const startIdx = items.length;
      for (let i = 0; i < checked.length; i++) {
        const it = checked[i];
        await apiRequest("POST", `/api/app/properties/${propertyId}/renovation-items`, {
          itemType: it.itemType,
          itemName: it.itemName,
          estimatedDays: defaultDays[it.itemType] || 7,
          sortOrder: startIdx + i,
          budget: it.budget > 0 ? String(Math.round(it.budget)) : null,
          bufferPercent: String(it.bufferPercent),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items"] });
      toast({ title: `${checked.length} item${checked.length !== 1 ? "s" : ""} imported from underwriting` });
      setShowPullFromUW(false);
    } catch {
      toast({ title: "Failed to import items", variant: "destructive" });
    } finally {
      setPullingFromUW(false);
    }
  }

  const completedCount = items.filter((i) => i.completed).length;
  const totalCount = items.length;

  const totalBudget = items.reduce((sum, item) => sum + (item.budget ? parseFloat(item.budget) || 0 : 0), 0);
  const totalBufferedBudget = items.reduce((sum, item) => {
    const base = item.budget ? parseFloat(item.budget) || 0 : 0;
    const pct = item.bufferPercent ? parseFloat(item.bufferPercent) || 0 : 0;
    return sum + base * (1 + pct / 100);
  }, 0);
  const totalSpent = items.reduce((sum, item) => sum + (expensesByItem[item.id] || 0), 0);
  const effectiveTotalBudget = totalBufferedBudget > 0 ? totalBufferedBudget : totalBudget;
  const budgetPct = effectiveTotalBudget > 0 ? Math.round((totalSpent / effectiveTotalBudget) * 100) : 0;
  const overBudgetPct = budgetPct > 100 ? budgetPct - 100 : 0;
  const suggestedBuffer = overBudgetPct > 0 ? overBudgetPct + 3 : 0;

  return (
    <div className="space-y-6">
      <BulkAddItemsDialog
        open={showBulkAdd}
        onClose={() => setShowBulkAdd(false)}
        existingTypes={existingTypes}
        defaultDays={defaultDays}
        onAdd={handleBulkAdd}
      />
      <AddExpenseDialog
        propertyId={propertyId}
        open={showAddExpense}
        onClose={() => setShowAddExpense(false)}
        items={items}
        expensesByItem={expensesByItem}
        onExpenseAdded={handleExpenseAdded}
      />
      <DocumentUploadDialog
        open={uploadDialog.open}
        onClose={() => { setUploadDialog({ open: false, mode: uploadDialog.mode }); setAutoBidFile(null); }}
        propertyId={propertyId}
        mode={uploadDialog.mode}
        defaultRenovationItemId={uploadDialog.renovationItemId ?? null}
        autoFile={uploadDialog.mode === "bid" ? autoBidFile : null}
        onSuccess={() => { queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] }); queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "renovation-items"] }); }}
      />

      {manageDocId !== null && (
        <ManageDocumentDialog
          open={manageDocId !== null}
          onClose={() => setManageDocId(null)}
          documentId={manageDocId}
        />
      )}

      <AlertDialog open={showStartPrompt} onOpenChange={(open) => { if (!open) handleDismissStartPrompt(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Hammer className="h-5 w-5 text-amber-500" />
              Start Renovation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You've logged your first expense for this renovation. The renovation status is currently set to "Not Started." Would you like to move it to "In Progress"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDismissStartPrompt} data-testid="button-dismiss-start-prompt">Not Now</AlertDialogCancel>
            <AlertDialogAction disabled={readOnly} onClick={handleConfirmInProgress} data-testid="button-confirm-in-progress">Yes, Move to In Progress</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showBudgetPaidPrompt} onOpenChange={setShowBudgetPaidPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Budget Fully Paid
            </AlertDialogTitle>
            <AlertDialogDescription>
              We notice your budget has now been paid in full. Would you like to mark this renovation as complete?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-dismiss-budget-paid">Not Yet</AlertDialogCancel>
            <AlertDialogAction disabled={readOnly} onClick={handleConfirmComplete} data-testid="button-confirm-complete">Yes, Mark as Complete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showLaunchPrompt} onOpenChange={setShowLaunchPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-blue-500" />
              Proceed with Launch?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to automatically move this property to the Launch phase and begin the launch process?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-dismiss-launch">Stay in Renovations</AlertDialogCancel>
            <AlertDialogAction disabled={readOnly} onClick={handleConfirmLaunch} data-testid="button-confirm-launch">Yes, Proceed with Launch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showLockPrompt} onOpenChange={setShowLockPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-blue-600" />
              Lock Renovation First
            </AlertDialogTitle>
            <AlertDialogDescription>
              To log expenses, the renovation must be locked to prevent changes to items, days, and budgets. Would you like to lock the renovation and proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-dismiss-lock-prompt">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={readOnly} onClick={handleLockAndLogExpense} data-testid="button-confirm-lock-and-log">Lock & Log Expense</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showPullFromUW} onOpenChange={(v) => !v && setShowPullFromUW(false)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-amber-600" />
              Pull from Underwriting CapEx
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">We've pulled your remodel and CapEx line items from your underwriting analysis. Uncheck any items you don't want to import, adjust budgets as needed, then click Import.</p>
          <p className="text-xs text-amber-600">Items already in your renovation plan are unchecked by default to avoid duplicates.</p>

          <div className="border rounded-lg overflow-hidden mt-2">
            <div className="grid grid-cols-[auto_1fr_120px_80px] gap-x-3 items-center px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
              <div className="w-5"></div>
              <div>Item</div>
              <div className="text-right">Budget</div>
              <div className="text-right">Buffer %</div>
            </div>
            {uwTransferItems.map((item) => (
              <div
                key={item.key}
                className={`grid grid-cols-[auto_1fr_120px_80px] gap-x-3 items-center px-3 py-2.5 border-b last:border-b-0 transition-colors ${item.checked ? "bg-amber-50" : "bg-white"}`}
              >
                <div className="w-5">
                  <Checkbox
                    checked={item.checked}
                    onCheckedChange={() => setUwTransferItems((prev) => prev.map((it) => it.key === item.key ? { ...it, checked: !it.checked } : it))}
                    data-testid={`checkbox-uw-transfer-${item.key}`}
                  />
                </div>
                <span className={`text-sm ${item.checked ? "text-gray-800" : "text-gray-400 line-through"}`}>{item.itemName}</span>
                <Input
                  type="number"
                  min={0}
                  step="100"
                  className="w-28 h-7 text-xs px-1 text-right ml-auto"
                  value={Math.round(item.budget)}
                  onChange={(e) => setUwTransferItems((prev) => prev.map((it) => it.key === item.key ? { ...it, budget: parseFloat(e.target.value) || 0 } : it))}
                  disabled={!item.checked}
                  data-testid={`input-uw-transfer-budget-${item.key}`}
                />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  className="w-16 h-7 text-xs px-1 text-right ml-auto"
                  value={item.bufferPercent}
                  onChange={(e) => setUwTransferItems((prev) => prev.map((it) => it.key === item.key ? { ...it, bufferPercent: parseFloat(e.target.value) || 0 } : it))}
                  disabled={!item.checked}
                  data-testid={`input-uw-transfer-buffer-${item.key}`}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center mt-2 text-sm">
            <span className="text-gray-500">Selected: {uwTransferItems.filter((it) => it.checked).length} items</span>
            <span className="font-semibold text-gray-900" data-testid="text-uw-transfer-total">
              Total: {fmt$(uwTransferItems.filter((it) => it.checked).reduce((s, it) => s + it.budget, 0))}
            </span>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPullFromUW(false)}>Cancel</Button>
            <Button onClick={confirmPullFromUW} disabled={readOnly || pullingFromUW || uwTransferItems.filter((it) => it.checked).length === 0} data-testid="button-confirm-pull-uw">
              {pullingFromUW ? "Importing..." : `Import ${uwTransferItems.filter((it) => it.checked).length} Items`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
        <Link href="/app/renovations"><span className="hover:text-gray-700 cursor-pointer" data-testid="link-breadcrumb-renovations">Renovations</span></Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-gray-900" data-testid="text-breadcrumb-property">{property.name}</span>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900" data-testid="text-renovation-title">{property.name}</h2>
          {property.address && <p className="text-sm text-gray-500 mt-0.5">{property.address}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={isLocked ? "default" : "outline"}
            size="sm"
            onClick={toggleLock}
            className={isLocked ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
            data-testid="button-toggle-lock"
          >
            {isLocked ? <Lock className="h-4 w-4 mr-1" /> : <Unlock className="h-4 w-4 mr-1" />}
            {isLocked ? "Locked" : "Lock"}
          </Button>
          {property.renovationStatus && (
            <Badge className={statusColor[property.renovationStatus] || "bg-gray-100 text-gray-600"} data-testid="badge-renovation-status">
              {statusLabel[property.renovationStatus] || property.renovationStatus}
            </Badge>
          )}
        </div>
      </div>

      {isLocked && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700" data-testid="text-locked-banner">
          <Lock className="h-4 w-4" />
          <span>This renovation is locked. Days and budgets cannot be edited. Unlock to make changes.</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="h-5 w-5 text-amber-600" />
              <h3 className="text-lg font-semibold text-gray-900">Plan Overview</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Renovation Status</label>
                <Select value={property.renovationStatus || ""} onValueChange={(v) => saveField("renovationStatus", v)}>
                  <SelectTrigger data-testid="select-renovation-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="complete">Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                <Input
                  type="date"
                  defaultValue={renovationStartDate}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val !== renovationStartDate) {
                      saveField("renovationStartDate", val || null);
                      recalcAndSaveLaunch(val, items, permitsRequired, permitDays);
                    }
                  }}
                  data-testid="input-renovation-start-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Switch
                    checked={permitsRequired}
                    onCheckedChange={(checked) => {
                      const effectivePermitDays = checked && !permitsRequired
                        ? (defaults?.defaultPermitDays ?? 45)
                        : permitDays;
                      if (checked && !permitsRequired && effectivePermitDays !== permitDays) {
                        updateMutation.mutate({ permitsRequired: checked, permitDays: effectivePermitDays });
                      } else {
                        saveField("permitsRequired", checked);
                      }
                      if (renovationStartDate) recalcAndSaveLaunch(renovationStartDate, items, checked, effectivePermitDays);
                    }}
                    data-testid="switch-permits-required"
                  />
                  <label className="text-sm font-semibold text-gray-700">Permits Required</label>
                </div>
                {permitsRequired && (
                  <div className="ml-1">
                    <label className="text-xs text-gray-500 mb-1 block">Permit Days</label>
                    <Input
                      type="number"
                      min={1}
                      defaultValue={permitDays}
                      className="w-32 text-sm"
                      disabled={isLocked}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 45;
                        saveField("permitDays", val);
                        if (renovationStartDate) recalcAndSaveLaunch(renovationStartDate, items, permitsRequired, val);
                      }}
                      data-testid="input-permit-days"
                    />
                    <p className="text-xs text-gray-400 mt-1">Typical: 30–60 days</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  <span className="inline-flex items-center gap-1 text-amber-700 font-bold">
                    Renovation Completion Date
                  </span>
                </label>
                <Input
                  type="date"
                  defaultValue={projectedLaunch}
                  key={projectedLaunch}
                  onBlur={(e) => { if (e.target.value !== projectedLaunch) saveField("projectedLaunchDate", e.target.value || null); }}
                  data-testid="input-projected-launch"
                />
                {renovationStartDate && items.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Auto-calculated: {items.reduce((s, i) => s + i.estimatedDays, 0)} item days
                    {permitsRequired ? ` + ${permitDays} permit days` : ""}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
              <Textarea
                defaultValue={property.renovationNotes || ""}
                placeholder="Track renovation progress, contractor details, scope of work..."
                className="min-h-[80px]"
                onBlur={(e) => { if (e.target.value !== (property.renovationNotes || "")) saveField("renovationNotes", e.target.value || null); }}
                data-testid="textarea-renovation-notes"
              />
            </div>
          </div>

          <div className="bg-white border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Hammer className="h-5 w-5 text-amber-600" />
                <h3 className="text-lg font-semibold text-gray-900">Renovation Items</h3>
                {totalCount > 0 && (
                  <Badge variant="outline" className="text-xs" data-testid="badge-progress">
                    {completedCount}/{totalCount} done
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <div className="flex items-center gap-2 mr-1">
                  <Switch
                    checked={showVoided}
                    onCheckedChange={setShowVoided}
                    data-testid="switch-show-voided-renovation-items"
                  />
                  <label className="text-xs text-gray-600 cursor-pointer" onClick={() => setShowVoided((v) => !v)}>
                    Show voided{showVoided && voidedItems.length > 0 ? ` (${voidedItems.length})` : ""}
                  </label>
                </div>
                {items.length > 0 && (
                  <Button size="sm" variant="outline" onClick={handleLogExpenseClick} data-testid="button-log-expense">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Log Expense
                  </Button>
                )}
                <Button disabled={readOnly} size="sm" variant="outline" onClick={() => setUploadDialog({ open: true, mode: "invoice" })} data-testid="button-upload-invoice">
                  <Plus className="h-4 w-4 mr-1" />
                  Upload Invoice
                </Button>
                {(() => {
                  const canUploadBid = (property.renovationStatus || "not_started") === "not_started";
                  return (
                    <span title={canUploadBid ? "Upload contractor bid" : "Bids can only be uploaded while renovation is Not Started."}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={readOnly || !canUploadBid}
                        onClick={() => canUploadBid && setUploadDialog({ open: true, mode: "bid" })}
                        data-testid="button-upload-bid"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Upload Bid
                      </Button>
                    </span>
                  );
                })()}
                {!isLocked && (
                  <>
                    <Button disabled={readOnly} size="sm" variant="outline" onClick={handlePullFromUnderwriting} data-testid="button-pull-from-uw">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Pull from Underwriting
                    </Button>
                    <Button disabled={readOnly} size="sm" variant="outline" onClick={() => setShowBulkAdd(true)} data-testid="button-add-item">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Items
                    </Button>
                  </>
                )}
              </div>
            </div>

            {itemsQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : allItems.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Hammer className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No renovation items yet. Click "Add Items" to build your plan.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <div className="space-y-0 min-w-[720px]">
                <div className="grid grid-cols-[1rem_1.25rem_minmax(160px,1fr)_3.5rem_5rem_3.5rem_6rem_5rem_3.5rem_1.75rem] gap-x-2 items-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
                  <div className="w-4"></div>
                  <div className="w-5"></div>
                  <div>Item</div>
                  <div className="w-14 text-right">Days</div>
                  <div className="w-20 text-right">Budget</div>
                  <div className="w-14 text-right">Buf %</div>
                  <div className="w-24 text-right">Buffered</div>
                  <div className="w-20 text-right">Spent</div>
                  <div className="w-14 text-right">Paid</div>
                  <div className="w-7"></div>
                </div>
                {items.map((item) => {
                  const itemBudget = item.budget ? parseFloat(item.budget) || 0 : 0;
                  const itemBufferPct = item.bufferPercent ? parseFloat(item.bufferPercent) || 0 : 0;
                  const itemBufferedBudget = itemBudget > 0 ? itemBudget * (1 + itemBufferPct / 100) : 0;
                  const itemSpent = expensesByItem[item.id] || 0;
                  const effectiveItemBudget = itemBufferedBudget > 0 ? itemBufferedBudget : itemBudget;
                  const itemPaidPct = effectiveItemBudget > 0 ? Math.round((itemSpent / effectiveItemBudget) * 100) : 0;
                  return (
                    <div
                      key={item.id}
                      draggable={!isLocked}
                      onDragStart={() => handleDragStart(item.id)}
                      onDragOver={(e) => handleDragOver(e, item.id)}
                      onDrop={() => handleDrop(item.id)}
                      onDragEnd={() => { setDraggedId(null); setDragOverId(null); }}
                      className={`grid grid-cols-[1rem_1.25rem_minmax(160px,1fr)_3.5rem_5rem_3.5rem_6rem_5rem_3.5rem_1.75rem] gap-x-2 items-center p-3 border-b transition-colors ${
                        dragOverId === item.id ? "border-amber-400 bg-amber-50" : item.completed ? "bg-green-50" : "bg-white"
                      }`}
                      data-testid={`renovation-item-${item.id}`}
                    >
                      <GripVertical className={`h-4 w-4 shrink-0 ${isLocked ? "text-gray-200" : "text-gray-300 cursor-grab"}`} />
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={() => handleToggleComplete(item)}
                        data-testid={`checkbox-item-${item.id}`}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm font-medium ${item.completed ? "line-through text-gray-400" : "text-gray-800"}`}>
                            {item.itemName}
                          </p>
                          {(() => {
                            const sourceDocId = item.documentId ?? item.bidDocumentId ?? null;
                            const sourceIsBid = !item.documentId && !!item.bidDocumentId;
                            if (!sourceDocId) return null;
                            return (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setManageDocId(sourceDocId); }}
                                className="text-gray-400 hover:text-orange-600 p-0.5 shrink-0"
                                title={sourceIsBid ? "Manage source bid (re-extract or void)" : "Manage source invoice (re-extract or void)"}
                                data-testid={`button-manage-document-renovation-${item.id}`}
                              >
                                <FileSearch className="h-3.5 w-3.5" />
                              </button>
                            );
                          })()}
                        </div>
                        {item.completed && item.completedDate && (
                          <p className="text-xs text-green-600">Completed {item.completedDate}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 w-14 justify-end">
                        <Input
                          type="number"
                          min={1}
                          className="w-14 h-7 text-xs px-1 text-right"
                          defaultValue={item.estimatedDays}
                          key={`days-${item.id}-${item.estimatedDays}`}
                          disabled={isLocked}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value) || 1;
                            if (val !== item.estimatedDays) {
                              updateItemMutation.mutate({ id: item.id, data: { estimatedDays: val } });
                              if (renovationStartDate) {
                                const updated = items.map((i) => i.id === item.id ? { ...i, estimatedDays: val } : i);
                                recalcAndSaveLaunch(renovationStartDate, updated, permitsRequired, permitDays);
                              }
                            }
                          }}
                          data-testid={`input-item-days-${item.id}`}
                        />
                      </div>
                      <div className="w-20 flex justify-end">
                        <Input
                          type="number"
                          min={0}
                          step="100"
                          className="w-20 h-7 text-xs px-1 text-right"
                          defaultValue={item.budget || ""}
                          key={`budget-${item.id}-${item.budget}`}
                          placeholder="$0"
                          disabled={isLocked}
                          onBlur={(e) => {
                            const val = e.target.value;
                            const current = item.budget || "";
                            if (val !== current) {
                              updateItemMutation.mutate({ id: item.id, data: { budget: val || null } });
                            }
                          }}
                          data-testid={`input-item-budget-${item.id}`}
                        />
                      </div>
                      <div className="w-14 flex justify-end">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step="1"
                          className="w-14 h-7 text-xs px-1 text-right"
                          defaultValue={item.bufferPercent ?? "0"}
                          key={`buffer-${item.id}-${item.bufferPercent}`}
                          placeholder="0"
                          disabled={isLocked}
                          onBlur={(e) => {
                            const val = e.target.value;
                            const current = item.bufferPercent ?? "0";
                            if (val !== current) {
                              updateItemMutation.mutate({ id: item.id, data: { bufferPercent: val || null } });
                            }
                          }}
                          data-testid={`input-item-buffer-${item.id}`}
                        />
                      </div>
                      <div className="w-24 text-right">
                        <span className="text-xs text-gray-500" data-testid={`text-item-buffered-${item.id}`}>
                          {itemBufferedBudget > 0 ? fmt$(itemBufferedBudget) : itemBudget > 0 ? fmt$(itemBudget) : "—"}
                        </span>
                      </div>
                      <div className="w-20 text-right">
                        <span className={`text-xs font-medium ${itemSpent > 0 ? (effectiveItemBudget > 0 && itemSpent > effectiveItemBudget ? "text-red-600" : "text-gray-700") : "text-gray-400"}`} data-testid={`text-item-spent-${item.id}`}>
                          {itemSpent > 0 ? fmt$(itemSpent) : "$0"}
                        </span>
                      </div>
                      <div className="w-14 text-right">
                        {effectiveItemBudget > 0 ? (
                          <span className={`text-xs font-medium ${itemPaidPct > 100 ? "text-red-600" : itemPaidPct === 100 ? "text-green-600" : "text-gray-500"}`} data-testid={`text-item-pct-${item.id}`}>
                            {itemPaidPct}%
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300" data-testid={`text-item-pct-${item.id}`}>—</span>
                        )}
                      </div>
                      {!isLocked ? (
                        <Button disabled={readOnly || deleteItemMutation.isPending}
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-red-500 shrink-0"
                          onClick={() => deleteItemMutation.mutate(item.id)}
                          data-testid={`button-delete-item-${item.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <div className="w-7"></div>
                      )}
                    </div>
                  );
                })}

                {showVoided && voidedItems.map((item) => {
                  const restoreDocId = item.documentId ?? item.bidDocumentId;
                  return (
                    <div
                      key={`voided-${item.id}`}
                      className="grid grid-cols-[1rem_1.25rem_minmax(160px,1fr)_3.5rem_5rem_3.5rem_6rem_5rem_3.5rem_1.75rem] gap-x-2 items-center p-3 border-b bg-gray-50 text-gray-400"
                      data-testid={`renovation-item-voided-${item.id}`}
                    >
                      <div className="w-4"></div>
                      <div className="w-5"></div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium line-through">{item.itemName}</p>
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 text-gray-500" data-testid={`badge-voided-item-${item.id}`}>Voided</span>
                      </div>
                      <div className="w-14 text-right text-xs line-through">{item.estimatedDays}d</div>
                      <div className="w-20 text-right text-xs line-through">{item.budget ? fmt$(parseFloat(item.budget) || 0) : "—"}</div>
                      <div className="w-14 text-right text-xs line-through">{item.bufferPercent ? `${item.bufferPercent}%` : "—"}</div>
                      <div className="w-24 text-right text-xs line-through">—</div>
                      <div className="w-20 text-right text-xs line-through">—</div>
                      <div className="w-14 text-right text-xs line-through">—</div>
                      <div className="flex items-center justify-end gap-0.5 min-w-[3.5rem]">
                        {restoreDocId ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-gray-400 hover:text-orange-600 shrink-0"
                              onClick={() => setManageDocId(restoreDocId)}
                              title="Manage source document"
                              data-testid={`button-manage-document-renovation-${item.id}`}
                            >
                              <FileSearch className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-gray-400 hover:text-green-600 shrink-0"
                              onClick={() => restoreDocMutation.mutate(restoreDocId)}
                              disabled={readOnly || (restoreDocMutation.isPending)}
                              title="Restore source document and linked records"
                              data-testid={`button-restore-renovation-item-${item.id}`}
                            >
                              <Undo2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })}

                {items.length > 0 && (
                  <div className="grid grid-cols-[1rem_1.25rem_minmax(160px,1fr)_3.5rem_5rem_3.5rem_6rem_5rem_3.5rem_1.75rem] gap-x-2 items-center px-3 py-3 bg-gray-50 border-t-2 border-gray-300 font-semibold" data-testid="row-totals">
                    <div className="w-4"></div>
                    <div className="w-5"></div>
                    <div className="text-sm text-gray-700">Totals</div>
                    <div className="w-14 text-right text-xs text-gray-600">
                      {items.reduce((s, i) => s + i.estimatedDays, 0)}d
                      {permitsRequired ? <span className="text-gray-400"> +{permitDays}</span> : ""}
                    </div>
                    <div className="w-20 text-right text-xs text-gray-700" data-testid="text-total-budget">{totalBudget > 0 ? fmt$(totalBudget) : "$0"}</div>
                    <div className="w-14"></div>
                    <div className="w-24 text-right text-xs font-semibold text-amber-700" data-testid="text-total-buffered-budget">{totalBufferedBudget > 0 ? fmt$(totalBufferedBudget) : (totalBudget > 0 ? fmt$(totalBudget) : "$0")}</div>
                    <div className="w-20 text-right text-xs text-gray-700" data-testid="text-total-spent">{totalSpent > 0 ? fmt$(totalSpent) : "$0"}</div>
                    <div className="w-14 text-right">
                      {effectiveTotalBudget > 0 ? (
                        <span className={`text-xs font-bold ${budgetPct > 100 ? "text-red-600" : budgetPct === 100 ? "text-green-600" : "text-gray-700"}`} data-testid="text-total-pct">
                          {budgetPct}%
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300" data-testid="text-total-pct">—</span>
                      )}
                    </div>
                    <div className="w-7"></div>
                  </div>
                )}

                {effectiveTotalBudget > 0 && totalSpent > effectiveTotalBudget && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2" data-testid="text-over-budget-warning">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold">Over budget by {fmt$(totalSpent - effectiveTotalBudget)} ({budgetPct}% of buffered budget)</p>
                      <p className="text-xs mt-1">In future, we suggest you add a {suggestedBuffer}% buffer to your bids to account for cost overruns.</p>
                    </div>
                  </div>
                )}
              </div>
              </div>
            )}
          </div>

          <div className="bg-white border rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Project Expenses (CapEx)</h3>
            </div>
            {items.length === 0 ? (
              <p className="text-sm text-gray-400">Add renovation items first to log expenses against budget items.</p>
            ) : (
              <p className="text-sm text-gray-500">Expenses are linked to renovation items and flow into the property's P&L as CapEx.</p>
            )}
            <div className="mt-3">
              <Link href={`/app/financials/expenses?propertyId=${property.id}`}>
                <span className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline cursor-pointer" data-testid="link-property-expenses">
                  <ExternalLink className="h-4 w-4" />
                  View Property Expenses in Financials
                </span>
              </Link>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {renovationStartDate && projectedLaunch && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Renovation Completion</p>
              <p className="text-xl font-bold text-amber-900" data-testid="text-projected-launch">
                {new Date(projectedLaunch + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
              {totalCount > 0 && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-amber-700 mb-1">
                    <span>Progress</span>
                    <span>{completedCount}/{totalCount}</span>
                  </div>
                  <div className="w-full bg-amber-200 rounded-full h-1.5">
                    <div
                      className="bg-amber-600 h-1.5 rounded-full transition-all"
                      style={{ width: totalCount > 0 ? `${(completedCount / totalCount) * 100}%` : "0%" }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {totalBudget > 0 && (
            <div className="border rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Budget Summary</p>
              <div className="space-y-1.5 text-sm">
                {totalBufferedBudget > totalBudget && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Base Budget</span>
                    <span className="text-gray-600" data-testid="text-sidebar-base-budget">{fmt$(totalBudget)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Budget</span>
                  <span className="font-semibold text-gray-800" data-testid="text-sidebar-budget">{fmt$(effectiveTotalBudget)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Spent</span>
                  <span className={`font-semibold ${totalSpent > effectiveTotalBudget ? "text-red-600" : "text-gray-800"}`} data-testid="text-sidebar-spent">{fmt$(totalSpent)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Remaining</span>
                  <span className={`font-semibold ${effectiveTotalBudget - totalSpent < 0 ? "text-red-600" : "text-green-600"}`} data-testid="text-sidebar-remaining">{fmt$(effectiveTotalBudget - totalSpent)}</span>
                </div>
                <div className="pt-1.5 border-t mt-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Budget Used</span>
                    <span className={`text-lg font-bold ${budgetPct > 100 ? "text-red-600" : budgetPct >= 90 ? "text-amber-600" : "text-green-600"}`} data-testid="text-sidebar-pct">{budgetPct}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className={`h-2 rounded-full transition-all ${budgetPct > 100 ? "bg-red-500" : budgetPct >= 90 ? "bg-amber-500" : "bg-green-500"}`}
                      style={{ width: `${Math.min(budgetPct, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Quick Links</h3>
            <div className="space-y-2">
              <Link href={`/app/properties/${property.id}`}>
                <span className="flex items-center gap-2 text-xs text-blue-600 hover:underline cursor-pointer" data-testid="link-property-details">
                  <Building2 className="h-3.5 w-3.5" /> Property Details
                </span>
              </Link>
              <Link href={`/app/financials/expenses?propertyId=${property.id}`}>
                <span className="flex items-center gap-2 text-xs text-blue-600 hover:underline cursor-pointer" data-testid="link-financials">
                  <DollarSign className="h-3.5 w-3.5" /> Property Expenses
                </span>
              </Link>
              {property.floorPlanUrl && (
                <a href={property.floorPlanUrl} target="_blank" rel="noopener noreferrer">
                  <span className="flex items-center gap-2 text-xs text-blue-600 hover:underline cursor-pointer" data-testid="link-floor-plan">
                    <ExternalLink className="h-3.5 w-3.5" /> Floor Plan
                  </span>
                </a>
              )}
            </div>
          </div>

          {totalCount > 0 && (
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 text-sm mb-3">Item Summary</h3>
              <div className="space-y-1.5">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 text-xs">
                    {item.completed ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                    ) : (
                      <CircleDashed className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                    )}
                    <span className={item.completed ? "text-gray-400 line-through" : "text-gray-700"}>{item.itemName}</span>
                    <span className="ml-auto text-gray-400">{item.estimatedDays}d</span>
                  </div>
                ))}
                <div className="border-t pt-1.5 mt-1.5 flex justify-between text-xs font-medium text-gray-700">
                  <span>Total days</span>
                  <span>{items.reduce((s, i) => s + i.estimatedDays, 0)}{permitsRequired ? ` + ${permitDays} permit` : ""}</span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            <p className="font-semibold mb-1">About Renovations</p>
            <p className="text-xs">Track renovation progress for your property. Drag items to reorder by priority. Set budgets per item and log expenses to track spending. Lock the renovation to prevent accidental changes.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

type RenovationTab = "properties" | "defaults" | "settings";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function AddToRenovationsDialog({
  open,
  onClose,
  properties,
}: {
  open: boolean;
  onClose: () => void;
  properties: Property[];
}) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [step, setStep] = useState<"choose" | "pull" | "address">("choose");
  const [selectedId, setSelectedId] = useState<string>("");
  const [addressName, setAddressName] = useState("");
  const [addressValue, setAddressValue] = useState("");
  const [busy, setBusy] = useState(false);
  const bidInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setStep("choose");
      setSelectedId("");
      setAddressName("");
      setAddressValue("");
      setBusy(false);
    }
  }, [open]);

  const candidates = properties.filter(
    (p) => p.status === "underwriting" || p.status === "acquisition",
  );

  function moveExistingToRenovations() {
    const id = parseInt(selectedId);
    if (!id) {
      toast({ title: "Select a property to pull in", variant: "destructive" });
      return;
    }
    onClose();
    navigate(`/app/underwriting/${id}?moveToRenovations=1`);
  }

  async function createFromAddress() {
    if (!addressValue.trim()) {
      toast({ title: "Address is required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const res = await apiRequest("POST", "/api/app/properties", {
        name: (addressName.trim() || addressValue.trim()),
        address: addressValue.trim(),
        status: "renovations",
        renovationStatus: "not_started",
      });
      const prop = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({ title: "Property created in Renovations" });
      onClose();
      navigate(`/app/renovations/${prop.id}`);
    } catch (e) {
      toast({ title: "Failed to create property", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  }

  async function handleBidFile(f: File) {
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      const exRes = await apiRequest("POST", "/api/app/documents/extract-address", {
        fileName: f.name,
        fileDataUrl: dataUrl,
        mode: "bid",
      });
      const ex = await exRes.json();
      const extractedAddress: string = (ex.propertyAddress || "").trim();
      const fallbackName = f.name.replace(/\.[^.]+$/, "");
      const res = await apiRequest("POST", "/api/app/properties", {
        name: extractedAddress || fallbackName,
        address: extractedAddress || undefined,
        status: "renovations",
        renovationStatus: "not_started",
      });
      const prop = await res.json();
      setPendingBidFile(f);
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({
        title: extractedAddress ? "Property created from bid" : "Property created — review the address",
        description: "Confirm the extracted bid line items on the next screen.",
      });
      onClose();
      navigate(`/app/renovations/${prop.id}?uploadBid=1`);
    } catch (e) {
      toast({ title: "Failed to process bid", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && busy) return;
        if (!o) onClose();
      }}
    >
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="dialog-add-to-renovations"
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Add to Renovations</DialogTitle>
        </DialogHeader>

        {step === "choose" && (
          <div className="space-y-3">
            <button
              type="button"
              className="w-full flex items-start gap-3 p-3 border rounded-lg text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
              onClick={() => setStep("pull")}
              disabled={busy}
              data-testid="button-add-reno-pull"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <Building2 className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Pull from Underwriting / Acquisition</p>
                <p className="text-sm text-gray-500">Move a property you're underwriting or acquiring into Renovations.</p>
              </div>
            </button>

            <button
              type="button"
              className="w-full flex items-start gap-3 p-3 border rounded-lg text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
              onClick={() => setStep("address")}
              disabled={busy}
              data-testid="button-add-reno-address"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Enter Address</p>
                <p className="text-sm text-gray-500">Create a new property directly in Renovations.</p>
              </div>
            </button>

            <button
              type="button"
              className="w-full flex items-start gap-3 p-3 border rounded-lg text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
              onClick={() => bidInputRef.current?.click()}
              disabled={busy}
              data-testid="button-add-reno-bid"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                {busy ? <Loader2 className="h-5 w-5 text-amber-600 animate-spin" /> : <Upload className="h-5 w-5 text-amber-600" />}
              </div>
              <div>
                <p className="font-medium text-gray-900">Upload Contractor Bid</p>
                <p className="text-sm text-gray-500">{busy ? "Reading the bid…" : "We'll detect the address, create the property, and pre-load the bid."}</p>
              </div>
            </button>
            <input
              ref={bidInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              data-testid="input-add-reno-bid-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) handleBidFile(f);
              }}
            />
          </div>
        )}

        {step === "pull" && (
          <div className="space-y-4">
            {candidates.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="text-no-pull-candidates">
                No properties are currently in Underwriting or Acquisition.
              </p>
            ) : (
              <div>
                <Label className="text-sm">Property</Label>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger className="mt-1" data-testid="select-pull-property"><SelectValue placeholder="Choose a property" /></SelectTrigger>
                  <SelectContent>
                    {candidates.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}{p.status === "acquisition" ? " · Acquisition" : " · Underwriting"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-2">Next you'll set your plan start date, permits, and choose which CapEx line items to transfer.</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("choose")} disabled={busy} data-testid="button-pull-back">Back</Button>
              <Button onClick={moveExistingToRenovations} disabled={busy || candidates.length === 0 || !selectedId} data-testid="button-pull-confirm">
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "address" && (
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Address</Label>
              <div className="mt-1">
                <AddressAutocomplete
                  value={addressValue}
                  onChange={(v) => setAddressValue(v)}
                  onSelect={(addr) => setAddressValue(addr)}
                  placeholder="123 Main St, City, ST 00000"
                  disabled={busy}
                  data-testid="input-reno-address-value"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Property name <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input className="mt-1" value={addressName} onChange={(e) => setAddressName(e.target.value)} placeholder="Defaults to the address" data-testid="input-reno-address-name" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("choose")} disabled={busy} data-testid="button-address-back">Back</Button>
              <Button onClick={createFromAddress} disabled={busy || !addressValue.trim()} data-testid="button-address-confirm">
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating…</> : "Create & open"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RenovationList() {
  usePageTitle("Renovations");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "properties", {
    tabs: ["properties", "defaults", "settings"],
  });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const properties = propertiesQuery.data || [];
  const renovationProperties = properties.filter(p => p.status === "renovations");
  const journeyStage = useJourneyStage();

  const statusColor: Record<string, string> = {
    not_started: "bg-gray-100 text-gray-600",
    in_progress: "bg-amber-100 text-amber-700",
    complete: "bg-green-100 text-green-700",
  };
  const statusLabel: Record<string, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    complete: "Complete",
  };

  const [showAddChooser, setShowAddChooser] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-renovations-heading" data-tour="renovations-heading">Renovations</h1>
          <p className="text-sm text-gray-500 mt-1">Manage renovation projects, defaults, and settings</p>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <Button onClick={() => setShowAddChooser(true)} data-testid="button-add-to-renovations" data-tour="renovations-add">
              <Plus className="h-4 w-4 mr-2" /> Add to Renovations
            </Button>
          )}
          <ExplainerVideoButton pageKey="renovations" />
          <GuideButton pageKey="renovations" />
        </div>
      </div>
      <GuideNudgeBanner pageKey="renovations" />

      <AddToRenovationsDialog open={showAddChooser} onClose={() => setShowAddChooser(false)} properties={properties} />

      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-0">
          {([
            { key: "properties" as RenovationTab, label: "Properties" },
            { key: "defaults" as RenovationTab, label: "Defaults" },
            { key: "settings" as RenovationTab, label: "Settings" },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-amber-500 text-amber-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`tab-renovations-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "properties" && (
        <>
          {propertiesQuery.isLoading ? (
            <div className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
          ) : renovationProperties.length === 0 ? (
            <JourneyEmptyState
              page="renovations"
              stage={journeyStage}
              icon={<Hammer className="h-10 w-10 text-gray-300" />}
              testIdPrefix="empty-renovations"
              primaryAction={readOnly ? undefined : { label: "Add to Renovations", onClick: () => setShowAddChooser(true) }}
            />
          ) : (
            <div className="space-y-3" data-tour="renovations-list">
              {renovationProperties.map((property) => (
                <Link
                  key={property.id}
                  href={`/app/renovations/${property.id}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  data-testid={`renovation-property-card-${property.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                      <Hammer className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{property.name}</p>
                      <p className="text-sm text-gray-500 truncate">{property.address || "No address"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {property.renovationLocked && (
                      <Lock className="h-4 w-4 text-blue-600" />
                    )}
                    {property.renovationStatus ? (
                      <Badge className={statusColor[property.renovationStatus] || "bg-gray-100 text-gray-600"}>
                        {statusLabel[property.renovationStatus] || "Not Started"}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500">Not Started</Badge>
                    )}
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === "defaults" && <DefaultsTab />}
      {activeTab === "settings" && <SettingsTab />}
    </div>
  );
}

export default function AppRenovations() {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("properties");
  const [matchDetail, params] = useRoute("/app/renovations/:id");
  const propertyId = matchDetail ? Number(params?.id) : null;
  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        {propertyId ? <RenovationDetail propertyId={propertyId} /> : <RenovationList />}
      </div>
    </AppLayout>
  );
}
